import { Box, PopoverProps, Stack, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { FC, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { CustomPopover } from '../../ui/CustomPopover';
import { DeleteOutline, EditOutlined, MoreHoriz, PaletteOutlined } from '@mui/icons-material';
import PersonaBlockItems from './PersonaBlockItems';
import { graphql, FragmentType, getFragmentData } from '../../gql';
import { useModalContext } from '../../modal/ModalContext';
import { useApiMutation } from '../../query/hooks';
import { ColorPicker } from '../../components/ColorPicker';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const personaBlockTile_blockFragment = graphql(`
	fragment personaBlockTile_blockFragment on PersonaBlock {
		id
		name
		description
		color
		items {
			...personaBlockItems_itemFragment
		}
	}
`);

const personaBlockTile_updateColor = graphql(`
	mutation personaBlockTile_updateColor($blockId: UUID!, $color: String!) {
		updatePersonaBlock(by: { id: $blockId }, data: { color: $color }) {
			ok
			errorMessage
		}
	}
`);

type PersonaBlockFragment = FragmentType<typeof personaBlockTile_blockFragment>;

type Props = Readonly<{
	blockFragment: PersonaBlockFragment;
	personaId: string;
}>;

const PersonaBlockTile: FC<Props> = ({ blockFragment, personaId }) => {
	const theme = useTheme();
	const intl = useIntl();
	const { setModal } = useModalContext();
	const [colorPopoverOpen, setColorPopoverOpen] = useState<boolean>(false);
	const [colorPopoverAnchorEl, setColorPopoverAnchorEl] = useState<PopoverProps['anchorEl']>(null);
	const ref = useRef<HTMLLIElement>(null);
	const forceCloseCustomPopoverEvent = useRef<Event>(new Event('forceClosePopover-color-change'));
	const [showEditIcon, setShowEditIcon] = useState<boolean>(false);
	const block = getFragmentData(personaBlockTile_blockFragment, blockFragment);
	const { mutate: updateColor } = useApiMutation(personaBlockTile_updateColor);
	const { openSnackbar } = useSnackbar();

	return (
		<Stack
			padding={2}
			borderRadius={2}
			border={`1px solid ${theme.palette.grey[300]}`}
			justifyContent={'space-between'}
			boxShadow={theme.customShadows.card}
			position={'relative'}
			onMouseEnter={() => setShowEditIcon(true)}
			onMouseLeave={() => setShowEditIcon(false)}
		>
			<Stack width={'100%'} minHeight={175} gap={2}>
				{showEditIcon && (
					<>
						<Box position={'absolute'} right={0} top={0} margin={2} zIndex={1} onClick={(e) => e.stopPropagation()}>
							<CustomPopover
								layoutIcon={<MoreHoriz />}
								forceCloseEvent={forceCloseCustomPopoverEvent.current}
								closeOnAction={false}
								options={[
									{
										icon: <EditOutlined sx={{ fontSize: 18, fill: theme.palette.grey[500] }} />,
										caption: <FormattedMessage id={'common.edit'} />,
										onAction: () => setModal({ type: 'updatePersonaBlock', payload: { personaId, blockId: block.id }, maxWidth: 'sm' }),
									},
									{
										icon: <PaletteOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
										caption: <FormattedMessage id={'persona.popover.set-color'} />,
										ref: ref,
										onAction: () => {
											const getBoundingClientRect = () => {
												return ref.current!.getBoundingClientRect();
											};

											setColorPopoverOpen((prevState) => !prevState);
											setColorPopoverAnchorEl({ getBoundingClientRect, nodeType: 1 });
										},
									},
									{
										icon: <DeleteOutline sx={{ fontSize: 18, fill: theme.palette.error.main }} />,
										caption: (
											<Typography component={'span'} color={theme.palette.error.main}>
												<FormattedMessage id={'common.delete'} />
											</Typography>
										),
										onAction: () => setModal({ type: 'deletePersonaBlock', payload: { id: block.id, name: block.name } }),
									},
								]}
							/>
						</Box>
						<ColorPicker
							open={colorPopoverOpen}
							defaultValue={block.color}
							anchorEl={colorPopoverAnchorEl}
							onClose={() => setColorPopoverOpen(false)}
							onChange={(event) => {
								updateColor(
									{ blockId: block.id, color: event.target.value },
									{
										onSuccess: (result) => {
											mutationResponseHandler({
												result: Boolean(result.updatePersonaBlock.ok),
												toastMessages: {
													error: intl.formatMessage({ id: 'common.error.default' }),
												},
												errorMessage: result.updatePersonaBlock.errorMessage,
												callback: () => {
													setColorPopoverOpen(false);
													window.dispatchEvent(forceCloseCustomPopoverEvent.current);
												},
												openSnackbar,
											});
										},
									},
								);
							}}
						/>
					</>
				)}
				<Typography variant={'h5'} fontWeight={700}>
					{block.name}
				</Typography>
				<Typography variant={'body2'} fontSize={'14px'} color={theme.palette.grey[500]}>
					{block.description}
				</Typography>
				<PersonaBlockItems itemsFragment={block.items} color={block.color} />
			</Stack>
		</Stack>
	);
};

export default PersonaBlockTile;
